import axios from "axios";
import { toast } from "react-toastify";
import { notifyError, notifySuccess } from "../helper/notify";

// ═══════════════════════════════════════════════════════════════════════
//  One place that turns "the request never reached the server" into a plain message for the WHOLE app. When there is no internet (or the server
//  cannot be reached, or the request timed out) the browser only says "Network Error", and each screen used to show that, or "Could not load …".
//  Now:
//    · the error is rewritten: error.message and error.response.data.message / .error all say NETWORK_MESSAGE (error.isNetworkError = true), so a
//      screen that shows `err.response?.data?.message || err.message` shows the connection message with no change to that screen;
//    · ONE toast "Check your internet connection and try again." appears (not one per failed request);
//    · going offline / back online is announced by the browser events.
//  A real server answer (400, 401, 404, 500 …) has a response and is left exactly as it was. A cancelled request is ignored.
//  Installed on the default axios (index.js) and on the shared `api` instance (utils/api.js), BEFORE that instance's own error handling.
// ═══════════════════════════════════════════════════════════════════════

export const NETWORK_MESSAGE = "Check your internet connection and try again.";

export const isNetworkFailure = (e) => !!e && !e.response && !axios.isCancel(e)
  && (["ERR_NETWORK", "ECONNABORTED", "ETIMEDOUT"].includes(e.code) || /network error|timeout/i.test(String(e.message || "")));

const onError = (error) => {
  if (isNetworkFailure(error)) {
    error.isNetworkError = true;
    error.message = NETWORK_MESSAGE;
    error.response = { status: 0, statusText: "", headers: {}, config: error.config, data: { message: NETWORK_MESSAGE, error: NETWORK_MESSAGE, isNetworkError: true } };
    notifyError(NETWORK_MESSAGE, { toastId: "network-error" });
  }
  return Promise.reject(error);
};

let browserEvents = false;
export function installNetworkGuard(...instances) {
  instances.forEach((inst) => { if (inst && inst.interceptors && !inst.__networkGuard) { inst.interceptors.response.use((r) => r, onError); inst.__networkGuard = true; } });
  if (!browserEvents && typeof window !== "undefined") {
    browserEvents = true;
    window.addEventListener("offline", () => notifyError("You are offline. Check your internet connection.", { toastId: "network-offline", autoClose: false }));
    window.addEventListener("online", () => { toast.dismiss("network-offline"); notifySuccess("You are back online.", { toastId: "network-online" }); });
  }
}
